import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown, LayoutGrid, ArrowRight } from 'lucide-react';
import { languages, getLanguageByCode, defaultLanguage } from '../../i18n/config.ts';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Utility for tailwind class merging
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const navItems = [
  { key: 'nav.products', href: '/products', isMegaMenu: true },
  { key: 'nav.about', href: '/about' },
  { key: 'nav.process', href: '/process' },
  { key: 'nav.blog', href: '/blog' },
  { key: 'nav.contact', href: '/contact' },
];

const megaMenuCategories = [
  { slug: 'tiny-house', name: 'Tiny House', desc: 'Minimal yaşam için akıllı ve özgür çözümler.' },
  { slug: 'kapsul-ev', name: 'Kapsül Ev', desc: 'Modern, kompakt ve konforlu yaşam alanları.' },
  { slug: 'moduler-ev', name: 'Modüler Ev', desc: 'İhtiyacınıza göre esnek ve genişletilebilir yapılar.' },
  { slug: 'celik-ev', name: 'Çelik Ev', desc: 'Dayanıklı çelik sistemlerle uzun ömürlü çözümler.' },
  { slug: 'prefabrik-ev', name: 'Prefabrik Ev', desc: 'Hızlı üretim, ekonomik ve güvenilir yapılar.' },
  { slug: 'bungalov', name: 'Bungalov', desc: 'Doğayla uyumlu estetik yaşam alanları.' },
  { slug: 'kabin-ev', name: 'Kabin Ev', desc: 'Doğada konforlu ve sıcak bir yaşam deneyimi.' },
  { slug: 'yuzen-ev', name: 'Yüzen Ev', desc: 'Su üzerinde özgün ve prestijli yaşam alanları.' },
  { slug: 'konteyner', name: 'Konteyner', desc: 'Pratik, hızlı kurulabilen modüler çözümler.' },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  const currentLang = i18n.language || defaultLanguage;
  const currentLanguage = getLanguageByCode(currentLang);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMegaMenuOpen(false);
  }, [location.pathname]);

  const handleLanguageChange = (langCode: string) => {
    let newPath = location.pathname;
    
    // Check if the path already starts with a supported language code
    const pathParts = newPath.split('/');
    const firstPart = pathParts[1];
    
    const isLangCode = languages.some(l => l.code === firstPart);
    
    if (isLangCode) {
      pathParts[1] = langCode;
      newPath = pathParts.join('/');
    } else {
      newPath = `/${langCode}${newPath === '/' ? '' : newPath}`;
    }

    i18n.changeLanguage(langCode);
    navigate(newPath);
    setIsLangMenuOpen(false);
  };

  const getLocalizedHref = (href: string) => {
    return `/${currentLang}${href}`;
  };

  const isActive = (href: string) => {
    return location.pathname.includes(href);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-[100] transition-all duration-300 bg-white border-b border-gray-100',
        isScrolled ? 'py-3 shadow-sm' : 'py-5'
      )}
      role="banner"
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between" role="navigation" aria-label="Main navigation">
          {/* Logo */}
          <Link 
            to={`/${currentLang}`} 
            className="flex items-center gap-2"
            aria-label="Muttiny Tiny House - Home"
          >
            <img 
              src="/logo.png" 
              alt="Muttiny Tiny House" 
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => {
              if (item.isMegaMenu) {
                return (
                  <div 
                    key={item.key}
                    className="relative group"
                    onMouseEnter={() => setIsMegaMenuOpen(true)}
                    onMouseLeave={() => setIsMegaMenuOpen(false)}
                  >
                    <button
                      className={cn(
                        'flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-[#C6A16B] py-6 -my-6',
                        isActive(item.href) ? 'text-[#C6A16B]' : 'text-gray-900'
                      )}
                      aria-expanded={isMegaMenuOpen}
                    >
                      {t(item.key)}
                      <ChevronDown className={cn("w-4 h-4 transition-transform", isMegaMenuOpen && "rotate-180")} />
                    </button>
                    
                    <AnimatePresence>
                      {isMegaMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-[calc(100%+1.5rem)] -left-48 w-[900px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden cursor-default"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-8 grid grid-cols-3 gap-6">
                            {megaMenuCategories.map(cat => (
                              <Link
                                key={cat.slug}
                                to={`/${currentLang}/category/${cat.slug}`}
                                onClick={() => setIsMegaMenuOpen(false)}
                                className="group/item p-4 rounded-xl hover:bg-[#FAF8F4] transition-colors flex items-start gap-4"
                              >
                                <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 group-hover/item:bg-white border border-gray-100 text-[#C6A16B]">
                                  <LayoutGrid className="w-5 h-5" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
                                    {cat.name}
                                    <ArrowRight className="w-3 h-3 opacity-0 group-hover/item:opacity-100 transition-opacity text-[#C6A16B] -ml-1" />
                                  </h4>
                                  <p className="text-xs text-gray-500 leading-relaxed">{cat.desc}</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                          <div className="bg-[#FAF8F4] p-6 border-t border-gray-100 flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-bold text-gray-900">Tüm Ürünleri Gör</h4>
                              <p className="text-xs text-gray-600 mt-1">Tüm ürün kategorilerini keşfedin ve size en uygun yaşam alanını bulun.</p>
                            </div>
                            <Link 
                              to={`/${currentLang}/products`}
                              onClick={() => setIsMegaMenuOpen(false)}
                              className="px-6 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 hover:border-[#C6A16B] hover:text-[#C6A16B] transition-colors shadow-sm"
                            >
                              Tüm ürünler
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <Link
                  key={item.key}
                  to={getLocalizedHref(item.href)}
                  className={cn(
                    'text-sm font-medium transition-colors hover:text-[#C6A16B]',
                    isActive(item.href) ? 'text-[#C6A16B]' : 'text-gray-900'
                  )}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {t(item.key)}
                </Link>
              );
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                aria-expanded={isLangMenuOpen}
                aria-label="Select language"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{currentLanguage.flag}</span>
                <span className="uppercase text-xs">{currentLang}</span>
                <ChevronDown className={cn(
                  'w-3 h-3 transition-transform',
                  isLangMenuOpen && 'rotate-180'
                )} />
              </button>

              <AnimatePresence>
                {isLangMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-[110]"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                          currentLang === lang.code
                            ? 'bg-[#FAF8F4] text-[#C6A16B]'
                            : 'text-gray-700 hover:bg-gray-50'
                        )}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                        {currentLang === lang.code && (
                          <span className="ml-auto text-[#C6A16B]">✓</span>
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* CTA Button */}
            <Link
              to={getLocalizedHref('/contact')}
              className="hidden sm:inline-flex items-center px-5 py-2.5 bg-[#A98573] text-white text-sm font-medium rounded-xl hover:bg-[#947463] transition-colors shadow-sm"
            >
              {t('nav.getQuote')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="container-custom py-4 space-y-2">
              {navItems.map((item) => {
                if (item.isMegaMenu) {
                  return (
                    <div key={item.key} className="space-y-1">
                      <button
                        onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                        className={cn(
                          'w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors',
                          isActive(item.href) || isMobileProductsOpen
                            ? 'bg-[#FAF8F4] text-[#C6A16B]'
                            : 'text-gray-900 hover:bg-gray-50'
                        )}
                      >
                        {t(item.key)}
                        <ChevronDown className={cn("w-5 h-5 transition-transform", isMobileProductsOpen && "rotate-180")} />
                      </button>
                      <AnimatePresence>
                        {isMobileProductsOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="pl-6 pr-4 py-2 space-y-1 border-l-2 border-gray-100 ml-6">
                              {megaMenuCategories.map(cat => (
                                <Link
                                  key={cat.slug}
                                  to={`/${currentLang}/category/${cat.slug}`}
                                  className="block py-2.5 px-3 text-sm text-gray-600 hover:text-[#C6A16B] hover:bg-gray-50 rounded-lg transition-colors"
                                >
                                  {cat.name}
                                </Link>
                              ))}
                              <Link
                                to={`/${currentLang}/products`}
                                className="block py-2.5 px-3 mt-2 text-sm font-bold text-[#C6A16B] hover:bg-[#FAF8F4] rounded-lg transition-colors"
                              >
                                Tüm ürünleri gör &rarr;
                              </Link>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.key}
                    to={getLocalizedHref(item.href)}
                    className={cn(
                      'block px-4 py-3 rounded-xl text-base font-medium transition-colors',
                      isActive(item.href)
                        ? 'bg-[#FAF8F4] text-[#C6A16B]'
                        : 'text-gray-900 hover:bg-gray-50'
                    )}
                    aria-current={isActive(item.href) ? 'page' : undefined}
                  >
                    {t(item.key)}
                  </Link>
                );
              })}
              <div className="pt-4 border-t border-gray-100 mt-4">
                <Link
                  to={getLocalizedHref('/contact')}
                  className="block w-full text-center px-4 py-3.5 bg-[#A98573] text-white font-medium rounded-xl hover:bg-[#947463] transition-colors shadow-sm"
                >
                  {t('nav.getQuote')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;
